export type NewsletterStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "sent"
  | "partial"
  | "failed"
  | "cancelled";

export interface NewsletterProductItem {
  id?: string;
  name: string;
  image?: string;
  price?: number;
  currency?: string;
  href?: string;
  badge?: string;
}

export type NewsletterAlign = "left" | "center" | "right";
export type NewsletterImagePosition = "left" | "right";

export interface NewsletterSocialLink {
  network: string;
  url: string;
}

export interface NewsletterBlock {
  /** Stable id used as React key and by drag-and-drop. */
  id?: string;
  type: string;
  text?: string;
  html?: string;
  src?: string;
  alt?: string;
  href?: string;
  label?: string;
  height?: number;
  layout?: string;
  items?: NewsletterProductItem[];
  campaign_id?: string;
  title?: string;
  subtitle?: string;
  logo_url?: string;
  store_name?: string;
  address?: string;
  unsubscribe_label?: string;
  // image-text
  image_position?: NewsletterImagePosition;
  // social
  social?: NewsletterSocialLink[];
  // coupon
  coupon_code?: string;
  coupon_description?: string;
  coupon_expires?: string;
  coupon_cta_label?: string;
  coupon_cta_href?: string;
  // per-block styles (allowlisted at render time)
  background?: string;
  text_color?: string;
  align?: NewsletterAlign;
  padding?: number;
  margin?: number;
  radius?: number;
}

export const newBlockId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

/** Ensures every block has a stable id (legacy/seeded blocks may not). */
export const withBlockIds = (blocks: NewsletterBlock[]): NewsletterBlock[] => {
  const seen = new Set<string>();
  return blocks.map((block) => {
    let id = block.id;
    while (!id || seen.has(id)) id = newBlockId();
    seen.add(id);
    return { ...block, id };
  });
};

export interface NewsletterTemplate {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  blocks: NewsletterBlock[];
  html_source?: string;
  is_system: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface Newsletter {
  id: string;
  tenant_id: string;
  name: string;
  subject: string;
  template_id?: string | null;
  blocks: NewsletterBlock[];
  html_body?: string;
  status: NewsletterStatus;
  group_ids: string[];
  scheduled_at?: string | null;
  send_started_at?: string | null;
  sent_at?: string | null;
  failure_reason?: string;
  tracking_enabled: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface NewsletterStats {
  status: NewsletterStatus;
  total: number;
  pending: number;
  sent: number;
  failed: number;
  skipped: number;
  unique_opens: number;
  unique_clicks: number;
}

export interface NewsletterRecipient {
  id: string;
  email: string;
  status: "pending" | "sent" | "failed" | "skipped";
  job_id?: string;
  error?: string;
  sent_at?: string | null;
  created_at: string;
}

export interface CampaignItemSummary {
  compound_id: string;
  name?: string;
  image?: string;
  price_pvp_with_vat?: number;
  currency: string;
}

export interface CampaignSummary {
  id: number;
  name: string;
  description?: string;
  is_global: boolean;
  starts_at: string;
  ends_at: string;
  items: CampaignItemSummary[];
}

export const NEWSLETTER_STATUS_LABELS: Record<NewsletterStatus, string> = {
  draft: "Rascunho",
  scheduled: "Agendada",
  sending: "A enviar",
  sent: "Enviada",
  partial: "Enviada com erros",
  failed: "Falhou",
  cancelled: "Cancelada",
};

export const NEWSLETTER_STATUS_CLASS: Record<NewsletterStatus, string> = {
  draft: "bg-gray-100 text-gray-700",
  scheduled: "bg-blue-100 text-blue-700",
  sending: "bg-amber-100 text-amber-700",
  sent: "bg-green-100 text-green-700",
  partial: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-700",
  cancelled: "bg-gray-200 text-gray-600",
};
